<template>
	<section class="v2to">

		<!-- ════════ HERO BANNER: Payment Model ════════ -->
		<div class="v2to__hero">
			<div class="v2to__hero-icon">
				<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>
			</div>
			<span class="v2to__hero-text">Payment model:</span>
			<span class="v2to__hero-pill">{{ currentPaymentModel ? currentPaymentModel.label : '—' }}</span>
			<span class="v2to__hero-hint">
				<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
				Read-only view
			</span>
		</div>

		<!-- ════════ SCHEDULE ════════ -->
		<div class="v2to__card">
			<div class="v2to__card-head">
				<div class="v2to__card-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4"/><path d="M3 10h18"/><path d="M8 2v4"/></svg>
				</div>
				<div>
					<h3 class="v2to__card-title">Schedule</h3>
					<p class="v2to__card-sub">When the campaign runs</p>
				</div>
			</div>

			<div class="v2to__schedule-cols">
				<div class="v2to__schedule-col">
					<label class="v2to__label">Start Date</label>
					<div class="v2to__chip v2to__chip--mono">{{ formatDate(instance.start_date) || '—' }}</div>
				</div>
				<div class="v2to__schedule-sep">—</div>
				<div class="v2to__schedule-col">
					<label class="v2to__label">End Date</label>
					<div class="v2to__chip v2to__chip--mono">{{ formatDate(instance.end_date) || '—' }}</div>
				</div>
				<div class="v2to__schedule-col">
					<label class="v2to__label">Ad Flight</label>
					<div class="v2to__chip v2to__chip--mono">{{ adFlight || '—' }} <span class="v2to__chip-units">{{ $tc('campaign.days_won', adFlight) }}</span></div>
				</div>
			</div>

			<div class="v2to__schedule-cols" style="margin-top: 16px;">
				<div class="v2to__schedule-col">
					<label class="v2to__label">Start Time</label>
					<div class="v2to__chip v2to__chip--mono">{{ (timeRangeHumanize && timeRangeHumanize[0]) || '00:00' }}</div>
				</div>
				<div class="v2to__schedule-sep">—</div>
				<div class="v2to__schedule-col">
					<label class="v2to__label">End Time</label>
					<div class="v2to__chip v2to__chip--mono">{{ (timeRangeHumanize && timeRangeHumanize[1]) || '23:59' }}</div>
				</div>
			</div>

			<div class="v2to__weekdays-section">
				<label class="v2to__label">Week Days</label>
				<div class="v2to__pill-group">
					<span
						v-for="day in weekDays"
						:key="day.key"
						class="v2to__pill"
						:class="{ 'v2to__pill--active': instance.schedule && instance.schedule[day.key] }"
					>{{ day.label }}</span>
				</div>
			</div>
		</div>

		<!-- ════════ AD DURATION & FREQUENCY ════════ -->
		<div class="v2to__card">
			<div class="v2to__card-head">
				<div class="v2to__card-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>
				</div>
				<div>
					<h3 class="v2to__card-title">Ad Duration &amp; Frequency</h3>
					<p class="v2to__card-sub">Playback length and capping</p>
				</div>
			</div>

			<div class="v2to__adfreq">
				<div class="v2to__adfreq-col">
					<label class="v2to__label">Message Duration</label>
					<div class="v2to__chip v2to__chip--mono">{{ instance.ad_duration || '—' }} <span class="v2to__chip-units">{{ $t('campaign.sec') }}</span></div>
					<p class="v2to__help">Length of one playback</p>
				</div>
				<div class="v2to__adfreq-col">
					<label class="v2to__label">Frequency Limit</label>
					<div class="v2to__chip v2to__chip--mono">{{ instance.ad_rate_per_hour || '—' }} <span class="v2to__chip-units">per hour</span></div>
				</div>
			</div>
		</div>

		<!-- ════════ BUDGET & BIDDING ════════ -->
		<div class="v2to__card">
			<div class="v2to__card-head">
				<div class="v2to__card-icon v2to__card-icon--accent">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
				</div>
				<div>
					<h3 class="v2to__card-title">Budget &amp; Bidding</h3>
					<p class="v2to__card-sub">Spend and bidding strategy</p>
				</div>
			</div>

			<div class="v2to__budget-grid">
				<div class="v2to__budget-col">
					<div class="v2to__budget-field">
						<label class="v2to__label">Total Budget</label>
						<div class="v2to__big-value">
							{{ instance.budget | parseFloat }}
							<span class="v2to__big-value-units">{{ instance.currency | formatCurrency }}</span>
						</div>
					</div>
					<div class="v2to__budget-field">
						<label class="v2to__label">Max Bid (per play)</label>
						<div class="v2to__big-value">
							{{ $options.filters.formatNumber(instance.max_bid, 2, '') || '—' }}
							<span class="v2to__big-value-units">{{ instance.currency | formatCurrency }}</span>
						</div>
					</div>
				</div>
				<div class="v2to__budget-col">
					<div v-if="instance.is_fixed_placement" class="v2to__fixed-cost">
						<label class="v2to__label">Fixed Cost</label>
						<div class="v2to__big-value v2to__big-value--accent">
							{{ $options.filters.formatNumber(instance.max_bid, 2, '') || '—' }}
							<span class="v2to__big-value-units">{{ instance.currency | formatCurrency }}</span>
						</div>
						<p class="v2to__help">Fixed placement is locked at this price</p>
					</div>
					<div v-else class="v2to__fixed-cost v2to__fixed-cost--off">
						<label class="v2to__label">Fixed Placement</label>
						<div class="v2to__chip v2to__chip--muted">Not enabled</div>
						<p class="v2to__help">Bid competes in real-time auction</p>
					</div>
				</div>
			</div>
		</div>

		<!-- ════════ ADDRESS + MAP ════════ -->
		<div class="v2to__map-grid">
			<!-- Address card -->
			<div class="v2to__card v2to__address-card">
				<div class="v2to__card-head">
					<div class="v2to__card-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
					</div>
					<div>
						<h3 class="v2to__card-title">Address</h3>
						<p class="v2to__card-sub">{{ areaCount }} location{{ areaCount === 1 ? '' : 's' }}</p>
					</div>
				</div>
				<div class="v2to__chip-list">
					<span
						v-for="area in displayedAreas"
						:key="area.id || area.name"
						class="v2to__area-chip"
						:class="{ 'v2to__area-chip--exclude': area.action === 'exclude' }"
					>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
						{{ areaDisplay(area) }}
					</span>
					<span v-if="!areaCount" class="v2to__chip v2to__chip--muted">No specific locations</span>
				</div>
			</div>

			<!-- Map -->
			<div class="v2to__map-wrap">
				<map-device
					:source-data="mapSource"
					:selected-areas-data="instance.areas"
					:search-query="searchQuery"
					:campaign-id="instance.id"
					without-control
					class="v2to__map"
					@searchListRequest="searchlistRequestHandler"
				/>
			</div>
		</div>

		<!-- ════════ AUDIENCE & DEVICE CHOICES ════════ -->
		<div class="v2to__card">
			<div class="v2to__card-head">
				<div class="v2to__card-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
				</div>
				<div>
					<h3 class="v2to__card-title">Broadcasting Points{{ isCPV ? ' & Audience' : '' }}</h3>
					<p class="v2to__card-sub">Targeting filters</p>
				</div>
			</div>

			<!-- CPV-only audience -->
			<template v-if="isCPV">
				<div class="v2to__audience-grid">
					<div class="v2to__audience-field">
						<label class="v2to__label">Age Range</label>
						<div class="v2to__chip v2to__chip--mono">
							<template v-if="instance.age && (instance.age[0] !== 0 || instance.age[1] !== 100)">
								{{ instance.age[0] }} — {{ instance.age[1] }} years
							</template>
							<template v-else>All ages</template>
						</div>
					</div>
					<div class="v2to__audience-field">
						<label class="v2to__label">Gender</label>
						<div class="v2to__chip">{{ genderLabel }}</div>
					</div>
				</div>
				<div class="v2to__divider" />
			</template>

			<!-- Resolutions -->
			<div class="v2to__field-block">
				<label class="v2to__label">Resolutions</label>
				<div class="v2to__chip-list">
					<span
						v-for="r in displayedResolutions"
						:key="r"
						class="v2to__chip v2to__chip--mono"
					>{{ r }}</span>
					<span v-if="!displayedResolutions.length" class="v2to__chip v2to__chip--muted">All resolutions</span>
				</div>
			</div>
		</div>

		<!-- ════════ PLATFORMS ════════ -->
		<div v-if="instance.platforms && instance.platforms.length" class="v2to__card">
			<div class="v2to__card-head">
				<div class="v2to__card-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
				</div>
				<div>
					<h3 class="v2to__card-title">Platforms</h3>
					<p class="v2to__card-sub">{{ instance.platforms.length }} network{{ instance.platforms.length === 1 ? '' : 's' }} selected</p>
				</div>
			</div>
			<div class="v2to__platforms-grid">
				<div
					v-for="p in platformList"
					:key="p.id || p.value"
					class="v2to__platform-card"
				>
					<div class="v2to__platform-name">{{ p.name || p.label }}</div>
					<div v-if="p.description || p.title" class="v2to__platform-sub">{{ p.description || p.title }}</div>
				</div>
			</div>
		</div>

		<!-- ════════ CAMPAIGN SUMMARY ════════ -->
		<div v-if="currentSelection" class="v2to__summary-card">
			<div class="v2to__card-head">
				<div class="v2to__card-icon v2to__card-icon--accent">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M7 16h8"/><path d="M7 11h12"/><path d="M7 6h3"/></svg>
				</div>
				<div>
					<h3 class="v2to__card-title">Campaign Summary</h3>
					<p class="v2to__card-sub">Calculated estimates</p>
				</div>
			</div>
			<div class="v2to__stats-grid">
				<div class="v2to__stat">
					<p class="v2to__stat-label">Pricing Model</p>
					<p class="v2to__stat-value v2to__stat-value--text">{{ currentPaymentModel ? currentPaymentModel.label : '—' }}</p>
				</div>
				<div class="v2to__stat">
					<p class="v2to__stat-label">Days</p>
					<p class="v2to__stat-value">{{ adFlight || '—' }}</p>
				</div>
				<div class="v2to__stat">
					<p class="v2to__stat-label">Total Plays</p>
					<p class="v2to__stat-value">{{ currentSelection.plays && currentSelection.plays.value || 0 }}</p>
				</div>
				<div class="v2to__stat">
					<p class="v2to__stat-label">Impressions</p>
					<p class="v2to__stat-value">{{ currentSelection.impressions && currentSelection.impressions.value || 0 }}</p>
				</div>
				<div class="v2to__stat">
					<p class="v2to__stat-label">Selected</p>
					<p class="v2to__stat-value">{{ currentSelection.inventory && currentSelection.inventory.value || 0 }}</p>
				</div>
				<div class="v2to__stat">
					<p class="v2to__stat-label">Available</p>
					<p class="v2to__stat-value">{{ currentSelection.inventory && currentSelection.inventory.total || 0 }}</p>
				</div>
			</div>
			<div class="v2to__summary-cta">
				<button
					class="v2to__btn v2to__btn--outline"
					:disabled="!instance.platforms || !instance.platforms.length"
					@click="isInventoryModalShowing = true"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M7 16h8"/><path d="M7 11h12"/><path d="M7 6h3"/></svg>
					{{ $t('campaign.open_inventory_modal') }}
				</button>
			</div>
		</div>

		<!-- ════════ INVENTORY MODAL ════════ -->
		<V2InventoryDetailsModal
			v-if="isInventoryModalShowing"
			:instance="instance"
			:current-selection="currentSelection"
			:show-additional-value="true"
			:impressions-per-day="0"
			:devices-uptime="{}"
			@update-required="updateMapAndStats"
			@close="isInventoryModalShowing = false"
		/>
	</section>
</template>


<script>
import moment from 'moment';
import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'underscore';

import MapDevice from '@/components/map-device';
import V2InventoryDetailsModal from '@/pages-v2/requests/inventory-details-modal/index.vue';

import { PAYMENT_MODEL_CPV } from '@/constants/payment_models';
import { getDevicesFilter, objectToQuery } from '@/utilites';

export default {
	name: 'V2TabOrder',

	components: {
		MapDevice,
		V2InventoryDetailsModal,
	},

	props: {
		instance: { type: Object, required: true },
		resource: { type: String, default: 'request' },
	},

	data() {
		return {
			isInventoryModalShowing: false,
			mapSource: { type: 'FeatureCollection', features: [] },
			searchQuery: '',
			searchListRequest: [],
			currentSelection: {
				plays: { value: 0, total: 0 },
				impressions: { value: 0, total: 0 },
				inventory: { value: 0, total: 0, excel_url: '' },
				price: { min: 0, max: 0, avg: 0 },
				specs: [],
				total_max_budget: 0,
			},
		};
	},

	computed: {
		...mapState('app', { appSide: 'side' }),
		...mapState('extra', ['paymentModels']),
		...mapGetters('extra', ['getPaymentModelByValue', 'getBroadcastTypeByValue']),

		isCPV() {
			return this.instance && this.instance.payment_model === PAYMENT_MODEL_CPV;
		},

		currentPaymentModel() {
			if (!this.instance || !this.instance.payment_model) return null;
			try {
				return this.getPaymentModelByValue(this.instance.payment_model);
			} catch (e) {
				return null;
			}
		},

		adFlight() {
			if (!this.instance || !this.instance.start_date || !this.instance.end_date) return null;
			const start = moment(this.instance.start_date);
			const end = moment(this.instance.end_date);
			const diff = end.diff(start, 'days') + 1;
			return isNaN(diff) ? null : diff;
		},

		timePeriod() {
			if (!this.instance || !this.instance.start_date || !this.instance.end_date) return '';
			return `${this.formatDate(this.instance.start_date)} — ${this.formatDate(this.instance.end_date)}`;
		},

		timeRangeHumanize() {
			if (!this.instance || !Array.isArray(this.instance.time_range)) return ['00:00', '23:59'];
			const fmt = (secs) => {
				const s = secs || 0;
				const h = Math.floor(s / 3600);
				const m = Math.floor((s % 3600) / 60);
				return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
			};
			return [fmt(this.instance.time_range[0]), fmt(this.instance.time_range[1])];
		},

		weekDays() {
			return [
				{ key: 'mon', label: 'Mon' },
				{ key: 'tue', label: 'Tue' },
				{ key: 'wed', label: 'Wed' },
				{ key: 'thu', label: 'Thu' },
				{ key: 'fri', label: 'Fri' },
				{ key: 'sat', label: 'Sat' },
				{ key: 'sun', label: 'Sun' },
			];
		},

		areaCount() {
			return (this.instance && this.instance.areas && this.instance.areas.length) || 0;
		},

		displayedAreas() {
			if (!this.instance || !Array.isArray(this.instance.areas)) return [];
			return this.instance.areas.slice(0, 12);
		},

		displayedResolutions() {
			if (!this.instance || !Array.isArray(this.instance.resolutions)) return [];
			return this.instance.resolutions.filter(r => r && r !== 'all');
		},

		genderLabel() {
			const g = this.instance && this.instance.gender;
			if (!g || g === 'all') return 'All genders';
			if (g === 'male' || g === 'm') return 'Male';
			if (g === 'female' || g === 'f') return 'Female';
			return g;
		},

		platformList() {
			if (!this.instance || !this.instance.platforms || !this.instance.platforms.length) return [];
			const networks = (this.appSide === 'ssp'
				? this.$store.state.app.sspNetworks
				: this.$store.state.app.dspNetworks) || [];
			return networks
				.filter(n => this.instance.platforms.includes(n.id))
				.map(n => ({ id: n.id, name: n.name, description: n.description }));
		},

		devicesFilter() {
			let filter = getDevicesFilter(this.instance, true);
			return objectToQuery(filter);
		},
	},

	async mounted() {
		await this.updateMapAndStats();
	},

	watch: {
		devicesFilter: {
			handler() {
				this.debouncedUpdateMapAndStats();
			},
		},
	},

	created() {
		this.debouncedUpdateMapAndStats = _.debounce(this.updateMapAndStats, 1000);
	},

	methods: {
		...mapActions('devices', ['getStats']),

		formatDate(d) {
			if (!d) return '';
			try {
				return moment(d).format("DD MMM 'YY");
			} catch (e) {
				return '';
			}
		},

		searchlistRequestHandler(results) {
			this.searchListRequest = results;
		},

		async updateMapAndStats() {
			try {
				const stats = await this.getStats({ filter: this.devicesFilter });
				if (stats) {
					this.$set(this, 'currentSelection', stats);
				}
			} catch (e) {
				// Ignore stats errors — just show empty state
			}
		},

		/**
		 * Helper to show area name, address or coords in the template
		 */
		areaDisplay(area) {
			return area.name || area.address || `${area.lat?.toFixed(2)}, ${area.lng?.toFixed(2)}`;
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$card: $v2-card-bg
$bg: $v2-bg
$green: $v2-green
$orange: $v2-orange
$red: $v2-red
$blue: $v2-blue
$font: $v2-font
$mono: 'JetBrains Mono', 'Menlo', monospace

.v2to
	display: flex
	flex-direction: column
	gap: 16px
	font-family: $font
	font-size: 13px
	color: $text

// ─── Hero banner ───
.v2to__hero
	display: flex
	align-items: center
	gap: 12px
	padding: 14px 18px
	border-radius: 14px
	background: linear-gradient(to right, rgba($teal, 0.06), rgba($teal, 0.02), transparent)
	border: 1px solid rgba($teal, 0.15)
	flex-wrap: wrap

.v2to__hero-icon
	width: 36px
	height: 36px
	border-radius: 10px
	background: rgba($teal, 0.1)
	color: $teal
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 16px
		height: 16px

.v2to__hero-text
	font-size: 13px
	font-weight: 500
	color: $text

.v2to__hero-pill
	display: inline-flex
	align-items: center
	padding: 4px 12px
	border-radius: 9999px
	background: $card
	border: 1px solid rgba($teal, 0.3)
	font-size: 12px
	font-weight: 600
	color: $text

.v2to__hero-hint
	margin-left: auto
	font-size: 11px
	color: $muted
	display: inline-flex
	align-items: center
	gap: 4px

// ─── Card ───
.v2to__card
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: 16px
	padding: 22px 24px
	box-shadow: 0 1px 3px rgba(0,0,0,0.04)

.v2to__card-head
	display: flex
	align-items: center
	gap: 12px
	margin-bottom: 20px

.v2to__card-icon
	width: 36px
	height: 36px
	border-radius: 10px
	background: rgba($teal, 0.1)
	color: $teal
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 16px
		height: 16px

	&--accent
		background: rgba($purple, 0.1)
		color: $purple

.v2to__card-title
	font-size: 14px
	font-weight: 700
	color: $text

.v2to__card-sub
	font-size: 11px
	color: $muted
	margin-top: 2px

// ─── Labels & values ───
.v2to__label
	display: block
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $muted
	margin-bottom: 6px

.v2to__chip
	display: inline-flex
	align-items: center
	gap: 6px
	padding: 8px 14px
	border-radius: 10px
	background: rgba($bg, 0.5)
	border: 1px solid $border
	font-size: 13px
	color: $text
	font-weight: 500

	&--mono
		font-family: $mono

	&--muted
		color: $muted
		font-weight: 400

.v2to__chip-units
	color: $muted
	font-weight: 400
	font-size: 11px
	margin-left: 4px

.v2to__chip-list
	display: flex
	flex-wrap: wrap
	gap: 6px

.v2to__help
	font-size: 11px
	color: $muted
	margin-top: 6px

.v2to__divider
	height: 1px
	background: rgba($border, 0.6)
	margin: 20px 0

// ─── Schedule ───
.v2to__schedule-cols
	display: flex
	align-items: flex-end
	gap: 12px
	flex-wrap: wrap

.v2to__schedule-col
	min-width: 140px

.v2to__schedule-sep
	color: $muted
	font-size: 16px
	padding-bottom: 12px
	flex-shrink: 0

.v2to__weekdays-section
	margin-top: 20px

.v2to__pill-group
	display: flex
	gap: 6px
	flex-wrap: wrap

.v2to__pill
	padding: 6px 14px
	border-radius: 9999px
	font-size: 11px
	font-weight: 600
	border: 1px solid $border
	color: $muted
	background: $card

	&--active
		background: $teal
		color: white
		border-color: $teal

// ─── Ad Duration & Frequency ───
.v2to__adfreq
	display: flex
	gap: 40px
	flex-wrap: wrap

.v2to__adfreq-col
	min-width: 0

// ─── Budget ───
.v2to__budget-grid
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 24px

	@media (max-width: 720px)
		grid-template-columns: 1fr

.v2to__budget-col
	display: flex
	flex-direction: column
	gap: 16px

.v2to__budget-field,
.v2to__fixed-cost
	min-width: 0

.v2to__fixed-cost
	padding: 16px
	border-radius: 12px
	background: rgba($purple, 0.04)
	border: 1px solid rgba($purple, 0.15)

	&--off
		background: rgba($bg, 0.5)
		border-color: $border

.v2to__big-value
	font-size: 20px
	font-weight: 700
	color: $text
	font-family: $mono
	display: inline-flex
	align-items: baseline
	gap: 6px

	&--accent
		color: $purple

.v2to__big-value-units
	font-size: 12px
	color: $muted
	font-weight: 500

// ─── Map grid ───
.v2to__map-grid
	display: grid
	grid-template-columns: 2fr 3fr
	gap: 16px

	@media (max-width: 900px)
		grid-template-columns: 1fr

.v2to__address-card
	display: flex
	flex-direction: column

.v2to__area-chip
	display: inline-flex
	align-items: center
	gap: 6px
	padding: 6px 12px
	border-radius: 9999px
	background: rgba($teal, 0.08)
	color: $teal
	font-size: 12px
	font-weight: 500

	svg
		width: 12px
		height: 12px

	&--exclude
		background: rgba($red, 0.08)
		color: $red

.v2to__map-wrap
	border-radius: 16px
	overflow: hidden
	border: 1px solid rgba($border, 0.6)
	box-shadow: 0 1px 3px rgba(0,0,0,0.04)
	height: 420px
	position: relative

.v2to__map
	position: absolute
	top: 0
	left: 0
	right: 0
	bottom: 0

	::v-deep
		.mgl-map-wrapper,
		.mgl-map-wrapper > div,
		.map-container,
		.mapboxgl-map,
		.mapboxgl-canvas-container,
		.mapboxgl-canvas
			width: 100% !important
			height: 100% !important

		.mapboxgl-ctrl-geocoder
			display: none !important

		.mapboxgl-ctrl-bottom-left,
		.mapboxgl-ctrl-bottom-right
			display: none

// ─── Audience ───
.v2to__audience-grid
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 20px

	@media (max-width: 600px)
		grid-template-columns: 1fr

.v2to__field-block
	display: block

// ─── Platforms ───
.v2to__platforms-grid
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 8px

	@media (max-width: 720px)
		grid-template-columns: 1fr

.v2to__platform-card
	padding: 12px 14px
	border-radius: 10px
	border: 1px solid $border
	background: rgba($bg, 0.4)

.v2to__platform-name
	font-size: 13px
	font-weight: 600
	color: $text

.v2to__platform-sub
	font-size: 11px
	color: $muted
	margin-top: 2px

// ─── Summary ───
.v2to__summary-card
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: 16px
	padding: 22px 24px
	box-shadow: 0 1px 3px rgba(0,0,0,0.04)
	background: linear-gradient(135deg, $card, $card, rgba($teal, 0.03))

.v2to__stats-grid
	display: grid
	grid-template-columns: repeat(6, 1fr)
	gap: 12px

	@media (max-width: 900px)
		grid-template-columns: repeat(3, 1fr)

	@media (max-width: 600px)
		grid-template-columns: repeat(2, 1fr)

.v2to__stat
	background: rgba($bg, 0.6)
	border: 1px solid rgba($border, 0.4)
	border-radius: 12px
	padding: 12px
	text-align: center

.v2to__stat-label
	font-size: 9px
	text-transform: uppercase
	letter-spacing: 0.06em
	font-weight: 600
	color: $muted

.v2to__stat-value
	margin-top: 4px
	font-family: $mono
	font-size: 18px
	font-weight: 700
	color: $text

	&--text
		font-family: $font
		font-size: 13px
		font-weight: 600

.v2to__summary-cta
	display: flex
	justify-content: center
	margin-top: 16px

// ─── Button ───
.v2to__btn
	display: inline-flex
	align-items: center
	gap: 6px
	height: 36px
	padding: 0 16px
	border-radius: 10px
	font-size: 12px
	font-weight: 600
	font-family: $font
	border: 1px solid transparent
	background: $card
	color: $text
	cursor: pointer
	transition: all 0.15s

	svg
		width: 14px
		height: 14px

	&:disabled
		opacity: 0.5
		cursor: not-allowed

	&--outline
		border-color: $border

		&:hover:not(:disabled)
			background: rgba($bg, 0.5)
</style>
